/**
 * Walking to a place rather than a person.
 *
 * A saved pin and a guided-arrival link are the same experience with different storage, and both
 * reuse the *entire* live screen — the same scene, the same range layering, the same beacon, the
 * same honesty indicators. A fixed destination is modelled as a peer whose position never
 * changes, so nothing downstream needs a special case.
 *
 * Neither destination ever reaches the server: a pin lives in `localStorage` (`lib/pins.ts`) and
 * a guided-arrival destination lives in the URL fragment, which browsers do not send.
 *
 * Choosing a place — the map picker and what to do with the point once you have it — lives in
 * `views/places.ts`. This file is only about the walking.
 */

import { type Destination, parseArrivalFragment } from '../lib/destination';
import { el } from '../lib/dom';
import { navigate } from '../lib/navigate';
import type { View } from '../lib/router';
import type { SessionState } from '../lib/session-model';
import { SensorHub, type SensorState } from '../sensors';
import { errorScreen } from '../ui/feedback';
import { liveScreen, type LiveScreen } from './session/live';
import { mountOnboarding } from './session/onboarding';

/** A fixed destination dressed as a room member, so the live screen needs no special case. */
function sessionForDestination(destination: Destination): SessionState {
  return {
    you: 'a',
    members: [
      {
        id: 'a',
        name: null,
        role: null,
        present: true,
        frame: null,
        receivedAt: null,
        staleness: 'none',
        converged: false,
      },
      {
        id: 'b',
        name: destination.label,
        role: null,
        present: true,
        frame: {
          lat: destination.lat,
          lng: destination.lng,
          acc: destination.accuracy ?? 0,
          heading: null,
          ts: destination.pinnedAt ?? Date.now(),
          alt: destination.altitude ?? null,
        },
        // A pin is not a signal: it is exactly as fresh now as when it was made.
        receivedAt: Date.now(),
        staleness: 'live',
        converged: false,
      },
    ],
    expiresAt: null,
    extensionsLeft: 0,
    capacity: 2,
    // A solo destination is not a room's rally point: nobody else is walking to it, and nobody
    // can move it. Dressing it as one would put a "move the meeting point" button on a screen
    // with no room behind it.
    rally: null,
    connection: 'open',
    ended: null,
  };
}

export type SoloKind = 'pin' | 'arrival';

/**
 * Shared machinery for both solo modes: onboarding, then the arrow, then arrival.
 *
 * One `SensorHub` serves both halves. It used to be two — one for the permission taps and one
 * for the walk — and the first was never stopped, so every trip to a pin left a `watchPosition`
 * running for the lifetime of the tab.
 */
export function runSolo(
  container: HTMLElement,
  kind: SoloKind,
  destination: Destination,
  extras: { onArrived: () => HTMLElement },
): () => void {
  let disposed = false;
  let live: LiveScreen | null = null;
  let stopOnboarding: (() => void) | null = null;
  let arrived = false;

  const sensors = new SensorHub({
    onChange: (state: SensorState) => {
      if (disposed || arrived) return;
      live?.update(session, state);
    },
  });

  const session = sessionForDestination(destination);

  const teardown = (): void => {
    stopOnboarding?.();
    stopOnboarding = null;
    sensors.stop();
    live?.destroy();
    live = null;
  };

  const start = (): void => {
    const screen = liveScreen({
      code: kind === 'pin' ? 'pinned spot' : 'arrival link',
      solo: true,
      // Shown at the handoff range, where the answer stops being a bearing and starts being
      // "look for that". A picture of the gate beats fifteen metres of arrow every time.
      handoffPhoto: destination.photo ?? null,
      onConverged: () => {
        if (arrived || disposed) return;
        arrived = true;
        teardown();
        container.replaceChildren(extras.onArrived());
      },
    });
    live = screen;
    container.replaceChildren(screen.element);

    sensors.start();
    // Paint once from whatever the hub already holds, rather than waiting for the first fix.
    live.update(session, sensors.current);
  };

  stopOnboarding = mountOnboarding(container, {
    sensors,
    peerName: destination.label,
    onDone: () => {
      stopOnboarding?.();
      stopOnboarding = null;
      if (!disposed) start();
    },
  });

  return () => {
    disposed = true;
    teardown();
  };
}

/* -------------------------------------------------------------------------- */
/* Guided arrival                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `/go#dst=lat,lng,label` — a destination baked into a link.
 *
 * The fragment never leaves the browser, so the place someone is walking to is not known to
 * Beeline at all. Opening the link starts the arrow experience pointed at it.
 */
export const arrivalView: View = (container, context) => {
  const destination = parseArrivalFragment(context.fragment);

  if (!destination) {
    container.replaceChildren(
      errorScreen({
        title: 'That link is incomplete',
        body: 'An arrival link carries its destination after the # — this one has lost it, probably to a messaging app that trimmed the address.',
        actionLabel: 'Make your own',
        onAction: () => navigate('/place?for=link'),
        secondaryLabel: 'Home',
        onSecondary: () => navigate('/'),
      }),
    );
    return;
  }

  return runSolo(container, 'arrival', destination, {
    onArrived: () =>
      arrivedScreen({
        title: `You are at ${destination.label}`,
        body: 'Nothing about this trip was recorded.',
        onDone: () => navigate('/'),
      }),
  });
};

/* -------------------------------------------------------------------------- */

export interface ArrivedOptions {
  title: string;
  body: string;
  onDone: () => void;
  onClear?: () => void;
  clearLabel?: string;
}

export function arrivedScreen(options: ArrivedOptions): HTMLElement {
  return el(
    'main',
    { class: 'screen screen--centered converged' },
    el('div', { class: 'converged__mark', 'aria-hidden': 'true' }),
    el('h1', { class: 'screen__title', text: options.title }),
    el('p', { class: 'screen__body', text: options.body }),
    el(
      'div',
      { class: 'screen__actions' },
      el(
        'button',
        {
          class: 'button button--primary button--block',
          type: 'button',
          onclick: () => options.onDone(),
        },
        'Done',
      ),
      options.onClear &&
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => options.onClear?.(),
          },
          options.clearLabel ?? 'Forget this place',
        ),
    ),
  );
}
